import React, { useEffect, useState, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useGoal } from "@/context/GoalContext";
import { X, ArrowRight, BellOff, ChevronDown, ChevronUp, Sparkles, Clock, Trophy, AlertTriangle, Target as TargetIcon, MessageSquare } from "lucide-react";
import DuckyAvatar from "@/components/app/DuckyAvatar";

const TIP_ICONS = {
  strategy: TargetIcon,
  timing: Clock,
  warning: AlertTriangle,
  milestone: Trophy,
  opportunity: Sparkles,
};

/**
 * Floating "Ducky Coach" avatar that surfaces goal-aware contextual tips.
 * Fetches on route change; respects the global pause toggle + per-popup dismiss.
 */
export default function DuckyCoach() {
  const loc = useLocation();
  const nav = useNavigate();
  const { goal, dismissPopup, togglePopups } = useGoal();
  const [popup, setPopup] = useState(null);
  const [open, setOpen] = useState(false);
  const [visible, setVisible] = useState(false);
  const [expandDeep, setExpandDeep] = useState(false);
  const [askText, setAskText] = useState("");
  const [askBusy, setAskBusy] = useState(false);
  const [askAnswers, setAskAnswers] = useState([]); // {q, a}
  const shownRef = useRef(new Set());

  // Only show for clients with an active goal
  const eligible = goal?.has_goal && !goal?.popups_paused;

  useEffect(() => {
    if (!eligible) { setPopup(null); setOpen(false); setVisible(false); return; }
    // Show the duck avatar globally (subtle) once a goal exists
    setVisible(true);
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/client/goal/popup", { params: { path: loc.pathname } });
        if (cancelled) return;
        const p = data?.popup;
        if (p && !shownRef.current.has(`${loc.pathname}|${p.id}`)) {
          setPopup(p);
          setOpen(true);
          setExpandDeep(false);
          setAskAnswers([]);
          setAskText("");
          shownRef.current.add(`${loc.pathname}|${p.id}`);
        } else {
          setPopup(null);
          setOpen(false);
        }
      } catch { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, [loc.pathname, eligible, goal?.updated_at]);

  if (!eligible || !visible || loc.pathname.startsWith("/city")) return null;

  const theme = goal?.theme;
  const primary = theme?.primary || "#F59E0B";

  return (
    <div
      className="fixed right-4 z-[60] flex flex-col items-end gap-2 pointer-events-none bottom-[calc(env(safe-area-inset-bottom,0px)+84px)] md:bottom-4"
      data-testid="ducky-coach"
    >
      {open && popup && (
        <div
          className="pointer-events-auto w-[320px] max-w-[calc(100vw-2rem)] rounded-2xl bg-white shadow-2xl border-2 overflow-hidden animate-[cq-coach-in_0.4s_cubic-bezier(0.16,1,0.3,1)]"
          style={{ borderColor: `${primary}55` }}
          data-testid="ducky-coach-popup"
        >
          {/* Header — hero snippet if we have one */}
          {goal?.hero_image_data_url && (
            <div className="relative h-20 w-full overflow-hidden">
              <img src={goal.hero_image_data_url} alt="" className="absolute inset-0 w-full h-full object-cover" />
              <div className="absolute inset-0" style={{ background: `linear-gradient(to top, ${primary}dd, transparent)` }} />
              <p className="absolute bottom-2 left-3 text-[10px] uppercase tracking-widest font-black text-white/95 flex items-center gap-1">
                <span>{theme?.emoji}</span> Coach Ducky · for your {theme?.label}
              </p>
              <button
                onClick={() => { setOpen(false); dismissPopup?.(popup.id); }}
                className="absolute top-2 right-2 h-6 w-6 rounded-full bg-white/90 hover:bg-white text-slate-700 grid place-items-center"
                data-testid="ducky-coach-dismiss"
              >
                <X size={12} />
              </button>
            </div>
          )}
          <div className="p-4">
            <p className="font-display font-black text-slate-900 text-base leading-tight">{popup.title}</p>
            <p className="text-sm text-slate-600 mt-1 leading-relaxed">{popup.body}</p>
            <div className="mt-3 flex items-center gap-2">
              <button
                onClick={() => { setOpen(false); nav(popup.route); }}
                className="inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-xs font-black uppercase tracking-widest text-white shadow"
                style={{ background: primary }}
                data-testid="ducky-coach-cta"
              >
                {popup.cta || "Show me"} <ArrowRight size={12} />
              </button>
              <button
                onClick={() => { setOpen(false); dismissPopup?.(popup.id); }}
                className="text-[11px] font-bold text-slate-500 hover:text-slate-800"
                data-testid="ducky-coach-skip"
              >
                Skip this one
              </button>
            </div>
            <button
              onClick={async () => { setOpen(false); await togglePopups?.(); }}
              className="mt-3 w-full inline-flex items-center justify-center gap-1 text-[11px] text-slate-400 hover:text-slate-700"
              data-testid="ducky-coach-pause"
            >
              <BellOff size={11} /> Pause coach tips
            </button>
          </div>
        </div>
      )}

      {/* Floating avatar — click to reopen popup or ping status */}
      <button
        onClick={() => { if (popup) setOpen((v) => !v); }}
        className="pointer-events-auto h-14 w-14 rounded-full shadow-2xl overflow-hidden border-4 border-white hover:scale-105 transition relative"
        style={{ background: `linear-gradient(135deg, ${primary}, ${theme?.accent || "#F59E0B"})` }}
        title={popup ? "Show tip" : "Coach on standby"}
        data-testid="ducky-coach-avatar"
      >
        <div className="h-full w-full flex items-center justify-center">
          <DuckyAvatar size={56} ring={false} bare className="drop-shadow" />
        </div>
        {popup && !open && (
          <span className="absolute -top-1 -right-1 h-3 w-3 rounded-full bg-rose-500 border-2 border-white animate-pulse" />
        )}
      </button>

      <style>{`
        @keyframes cq-coach-in { from{opacity:0;transform:translateY(20px) scale(0.95)} to{opacity:1;transform:translateY(0) scale(1)} }
      `}</style>
    </div>
  );
}
